import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";

import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { t } from "i18next";

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

type RowId = string | number;

type ConquestRow = {
    id: RowId;
    campaign_name?: string | null;
    ad_group_name?: string | null;
    keyword?: string | null;
    domain?: string | null;
    strategy?: string | null;
    accepted_variation_threshold?: string | number | null;
    max_cpc?: string | number | null;
    [key: string]: any;
};

type Props = {
    open: boolean;
    row: ConquestRow[] | ConquestRow | null;
    onClose: () => void;
    onSubmit: (data: ConquestRow[]) => void;
};

type CellChangeHandler = (id: RowId, field: keyof ConquestRow, value: any) => void;
const KPI_OPTIONS = ["Paid First", "Paid Top"] as const;
const CELL_WRAPPER_SX = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
} as const;

const INPUT_SX = {
    borderRadius: "6px",
    border: "1px solid rgba(145, 158, 171, 0.16)",
    display: "flex",
    alignItems: "center",
    width: "100%",
    minHeight: "36px",
    color: "rgba(51, 60, 71, 1)",
    px: 1,
    py: 0.5,
    "&:hover": {
        borderColor: "rgba(0, 108, 156, 0.40)",
        backgroundColor: "rgba(0, 108, 156, 0.03)",
    },
    "&.Mui-focused": {
        borderColor: "rgba(0, 108, 156, 0.40)",
        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
    },
} as const;

const LocalTextCell = memo(function LocalTextCell({
    id,
    field,
    value,
    type = "text",
    onCommit,
}: {
    id: RowId;
    field: keyof ConquestRow;
    value: string | number | null | undefined;
    type?: "text" | "number";
    onCommit: CellChangeHandler;
}) {
    const normalizedValue = value ?? "";
    const [localValue, setLocalValue] = useState<string>(String(normalizedValue));
    useEffect(() => {
        setLocalValue(String(value ?? ""));
    }, [value]);
    const commit = useCallback(() => {
        const currentValue = value ?? "";
        if (String(currentValue) !== localValue) {
            onCommit(id, field, localValue);
        }
    }, [field, id, localValue, onCommit, value]);
    return (
        <Box sx={CELL_WRAPPER_SX}>
            <TextField
                fullWidth
                type={type}
                value={localValue}
                onChange={(event) => setLocalValue(event.target.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.currentTarget.querySelector("input")?.blur();
                    }
                }}
                size="small"
                variant="standard"
                InputProps={{
                    disableUnderline: true,
                    sx: INPUT_SX,
                }}
            />
        </Box>
    );
});

const StrategyCell = memo(function StrategyCell({
    id,
    value,
    onCommit,
}: {
    id: RowId;
    value: string | null | undefined;
    onCommit: CellChangeHandler;
}) {
    return (
        <Box sx={CELL_WRAPPER_SX}>
            <Autocomplete
                size="small"
                options={KPI_OPTIONS}
                value={(value || null) as (typeof KPI_OPTIONS)[number] | null}
                onChange={(_, newValue) => onCommit(id, "strategy", newValue)}
                sx={{
                    width: "100%",
                    height: "36px",
                    "& .MuiFormControl-root": {
                        height: "36px",
                    },
                    "& .MuiInputBase-root": {
                        width: "100%",
                        height: "36px !important",
                        minHeight: "36px !important",
                        padding: "0 30px 0 8px !important",
                        borderRadius: "6px",
                        border: "1px solid rgba(145, 158, 171, 0.16)",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        color: "rgba(51, 60, 71, 1)",
                        "&:hover": {
                            borderColor: "rgba(0, 108, 156, 0.40)",
                            backgroundColor: "rgba(0, 108, 156, 0.03)",
                        },
                        "&.Mui-focused": {
                            borderColor: "rgba(0, 108, 156, 0.40)",
                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                        },
                    },
                    "& .MuiAutocomplete-input": {
                        height: "34px !important",
                        minHeight: "0 !important",
                        padding: "0 !important",
                        margin: "0 !important",
                        lineHeight: "34px !important",
                        boxSizing: "border-box",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                        top: "50%",
                        right: "6px",
                        transform: "translateY(-50%)",
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                        width: "28px",
                        height: "28px",
                        p: "2px",
                    },
                }}
                renderInput={(paramsInput) => (
                    <TextField
                        {...paramsInput}
                        fullWidth
                        size="small"
                        variant="standard"
                        InputProps={{
                            ...paramsInput.InputProps,
                            disableUnderline: true,
                        }}
                    />
                )}
            />
        </Box>
    );
});

export default function ModalCreateConquestMode({
    open,
    row,
    onClose,
    onSubmit,
}: Props) {
    const [rowsState, setRowsState] = useState<ConquestRow[]>([]);
    useEffect(() => {
        if (!open) return;
        if (Array.isArray(row)) {
            setRowsState(row.length > 0 ? [row[0]] : []);
            return;
        }
        setRowsState(row ? [row] : []);
    }, [open, row]);
    const handleCellChange = useCallback<CellChangeHandler>((id, field, value) => {
        setRowsState((previousRows) => {
            const index = previousRows.findIndex((currentRow) => currentRow.id === id);
            if (index === -1) return previousRows;
            const currentRow = previousRows[index];
            if (Object.is(currentRow[field], value)) return previousRows;
            const nextRows = previousRows.slice();
            nextRows[index] = {
                ...currentRow,
                [field]: value,
            };
            return nextRows;
        });
    }, []);
    const columns = useMemo<GridColDef<ConquestRow>[]>(
        () => [
            {
                field: "campaign_name",
                headerName: "Campaign",
                flex: 1,
                minWidth: 180,
                disableColumnMenu: true,
                sortable: false,
            },
            {
                field: "ad_group_name",
                headerName: "Ad Group",
                flex: 1,
                minWidth: 160,
                disableColumnMenu: true,
                sortable: false,
            },
            {
                field: "keyword",
                headerName: "Keyword",
                flex: 1,
                minWidth: 160,
                disableColumnMenu: true,
                sortable: false,
            },
            {
                field: "domain",
                headerName: "Competitor Domain name",
                flex: 1,
                minWidth: 180,
                disableColumnMenu: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<ConquestRow>) => (
                    <LocalTextCell
                        id={params.id}
                        field="domain"
                        value={params.row.domain}
                        onCommit={handleCellChange}
                    />
                ),
            },
            {
                field: "strategy",
                headerName: "KPI",
                minWidth: 150,
                maxWidth: 180,
                flex: 0.9,
                disableColumnMenu: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<ConquestRow>) => (
                    <StrategyCell
                        id={params.id}
                        value={params.row.strategy}
                        onCommit={handleCellChange}
                    />
                ),
            },
            {
                field: "accepted_variation_threshold",
                headerName: "Target",
                flex: 1,
                minWidth: 120,
                disableColumnMenu: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<ConquestRow>) => (
                    <LocalTextCell
                        id={params.id}
                        field="accepted_variation_threshold"
                        value={params.row.accepted_variation_threshold}
                        type="number"
                        onCommit={handleCellChange}
                    />
                ),
            },
            {
                field: "max_cpc",
                headerName: "Ceiling Max CPC",
                flex: 1,
                minWidth: 120,
                disableColumnMenu: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<ConquestRow>) => (
                    <LocalTextCell
                        id={params.id}
                        field="max_cpc"
                        value={params.row.max_cpc}
                        type="number"
                        onCommit={handleCellChange}
                    />
                ),
            },
        ],
        [handleCellChange]
    );
    const getRowId = useCallback((currentRow: ConquestRow) => currentRow.id, []);
    const handleSubmit = useCallback(() => {
        onSubmit(rowsState);
        onClose();
    }, [onClose, onSubmit, rowsState]);
    const title = useMemo(
        () => `Add new keyword${rowsState.length > 1 ? "s" : ""}`,
        [rowsState.length]
    );
    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    color: "#FFF",
                    maxHeight: "calc(100vh - 64px)",
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    p: 3,
                    backgroundColor: "#f4f7fb",
                }}
            >
                <Typography
                    sx={{
                        color: "#333C47",
                        fontFamily: '"Public Sans"',
                        fontSize: "18px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "100%",
                    }}
                >
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent
                sx={{
                    bgcolor: "#f4f7fb",
                    overflow: "visible",
                    px: 3,
                    py: 0,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "white",
                        height: 94,
                        width: "100%",
                        position: "relative",
                        "& .MuiDataGrid-columnHeader": {
                            backgroundColor: "rgba(250, 251, 251)",
                            border: "1px solid #e9ecee",
                        },
                        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                            outline: "none !important",
                        },
                        "& .MuiDataGrid-cell": {
                            border: "1px solid #e9ecee",
                        },
                        "& .MuiDataGrid-cell--pinnedLeft": {
                            backgroundColor: "white",
                        },
                        "& .MuiDataGrid-columnSeparator": {
                            color: "rgba(145, 158, 171, 0.18)",
                            visibility: "visible",
                        },
                    }}
                >
                    <DataGridPro
                        rows={rowsState}
                        columns={columns}
                        getRowId={getRowId}
                        rowHeight={52}
                        columnHeaderHeight={40}
                        disableRowSelectionOnClick
                        disableColumnFilter
                        disableColumnSelector
                        disableColumnReorder
                        hideFooter
                        rowBufferPx={52}
                        columnBufferPx={120}
                        sx={{
                            border: 0,
                            "& .MuiDataGrid-virtualScroller": {
                                overflow: "hidden",
                            },
                            "& .MuiDataGrid-virtualScrollerContent": {
                                minHeight: "52px !important",
                            },
                            "& .MuiDataGrid-virtualScrollerRenderZone": {
                                position: "relative",
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    bgcolor: "#f4f7fb",
                    px: 3,
                    pt: 2,
                    pb: 3,
                }}
            >
                <Button
                    variant="contained"
                    color="inherit"
                    onClick={handleSubmit}
                    sx={{
                        borderRadius: "8px",
                        background: "#181818",
                        color: "rgba(255, 255, 255, 1)",
                        border: "none",
                    }}
                >
                    {t("dashboard.global.brandPulse.activate")}
                </Button>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    sx={{
                        borderRadius: "8px",
                        background: "rgba(145, 158, 171, 0.12)",
                        color: "rgba(145, 158, 171, 1)",
                        border: "none",
                    }}
                >
                    {t("dashboard.global.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}